/*
 * yumaté navigation layer.
 * - In-page links land exactly below the sticky header, where the section colour starts.
 * - Clean URLs: yumate.de/faq instead of /#faq (Shopify redirects /faq, /herkunft,
 *   /zubereitung, /kaufen and /kontakt), and the contact page shows /kontakt.
 * - Highlights the header link of the section in view.
 * - FAQ answers open and close with a smooth height animation.
 * Respects prefers-reduced-motion.
 */
(function () {
  if (window.ymNavInit) return;
  window.ymNavInit = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* ---------- In-page navigation: exact landing, clean URLs, active header link ---------- */
  /* Sections that also have a short URL (yumate.de/faq etc. redirect to /#faq in Shopify). */
  var PRETTY = { herkunft: true, zubereitung: true, faq: true, kaufen: true };
  var onHome = location.pathname === '/';

  function scroller() {
    var pw = document.querySelector('.page-wrapper');
    if (pw && pw.scrollHeight > pw.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(pw).overflowY)) return pw;
    return document.scrollingElement || document.documentElement;
  }

  /* Height of the sticky header once it is stuck, minus 1px so the section colour starts right under it. */
  function headerOffset() {
    var header = document.querySelector('header');
    if (!header) return 0;
    var style = getComputedStyle(header);
    if (style.position !== 'sticky' && style.position !== 'fixed') return 0;
    var stuckTop = parseFloat(style.top) || 0;
    return Math.max(0, Math.floor(header.getBoundingClientRect().height + stuckTop) - 1);
  }

  function syncHeaderOffset() {
    document.documentElement.style.setProperty('--ym-header-offset', headerOffset() + 'px');
  }

  function scrollToId(id, smooth) {
    var el = document.getElementById(id);
    if (!el) return false;
    var box = scroller();
    var isRoot = box === document.scrollingElement || box === document.documentElement;
    var base = isRoot ? 0 : box.getBoundingClientRect().top;
    var top = box.scrollTop + el.getBoundingClientRect().top - base - headerOffset();
    box.scrollTo({ top: Math.max(0, Math.round(top)), behavior: smooth && !reduce ? 'smooth' : 'instant' });
    return true;
  }

  function cleanUrlFor(id) {
    if (!onHome) return location.pathname + location.search;
    return (PRETTY[id] ? '/' + id : '/') + location.search;
  }

  function handleAnchorClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target.closest && event.target.closest('a[href]');
    if (!link || (link.target && link.target !== '_self')) return;
    var url;
    try { url = new URL(link.getAttribute('href'), location.href); } catch (e) { return; }
    if (url.origin !== location.origin || !url.hash || url.hash.length < 2) return;
    var samePage = url.pathname === location.pathname || (onHome && url.pathname === '/');
    if (!samePage) return;
    var id = decodeURIComponent(url.hash.slice(1));
    if (!document.getElementById(id)) return;
    event.preventDefault();
    var go = function () {
      scrollToId(id, true);
      var next = cleanUrlFor(id);
      if (next !== location.pathname + location.search) history.pushState({ ymSection: id }, '', next);
    };
    var drawer = link.closest('header-drawer');
    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
      setTimeout(go, 320);
    } else {
      go();
    }
  }

  function handleInitialHash() {
    if (!location.hash || location.hash.length < 2) return;
    var id = decodeURIComponent(location.hash.slice(1));
    if (!document.getElementById(id)) return;
    var userMoved = false;
    ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach(function (type) {
      window.addEventListener(type, function () { userMoved = true; }, { once: true, passive: true });
    });
    var fix = function () { if (!userMoved) scrollToId(id, false); };
    fix();
    setTimeout(fix, 60);
    if (document.readyState !== 'complete') window.addEventListener('load', function () { fix(); setTimeout(fix, 120); }, { once: true });
    if (onHome) history.replaceState(history.state, '', cleanUrlFor(id));
  }

  function handlePopState() {
    if (!onHome) return;
    var id = location.pathname.replace(/^\/+|\/+$/g, '');
    setTimeout(function () {
      if (id && document.getElementById(id)) scrollToId(id, true);
      else if (!id) scroller().scrollTo({ top: 0, behavior: reduce ? 'instant' : 'smooth' });
    }, 0);
  }

  /* The contact page lives at /pages/contact in Shopify; show the short /kontakt (a redirect) instead. */
  function cleanContactUrl() {
    if (location.pathname === '/pages/contact' && !location.search) {
      history.replaceState(history.state, '', '/kontakt' + location.hash);
    }
  }

  /* Highlight the header link of the section currently in view. */
  function setupSpy() {
    if (!onHome || !('IntersectionObserver' in window)) return;
    var groups = {};
    document.querySelectorAll('a.menu-list__link[href*="#"], a.menu-drawer__menu-item[href*="#"]').forEach(function (a) {
      var href = a.getAttribute('href');
      var id = decodeURIComponent(href.slice(href.indexOf('#') + 1));
      if (id && document.getElementById(id)) (groups[id] = groups[id] || []).push(a);
    });
    var ids = Object.keys(groups);
    if (!ids.length) return;
    var visible = {};
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { visible[e.target.id] = e.isIntersecting; });
      var active = null;
      ids.forEach(function (id) { if (visible[id]) active = id; });
      ids.forEach(function (id) {
        groups[id].forEach(function (a) { a.toggleAttribute('data-ym-active', id === active); });
      });
    }, { rootMargin: '-35% 0px -60% 0px', threshold: 0 });
    ids.forEach(function (id) { io.observe(document.getElementById(id)); });
  }

  /* ---------- FAQ: animate open and close ---------- */
  function setupFaq(scope) {
    (scope || document).querySelectorAll('details.ym-faq__item').forEach(function (item) {
      if (item.ymFaq) return;
      item.ymFaq = true;
      var summary = item.querySelector('summary');
      var body = item.querySelector('.ym-faq__answer');
      if (!summary || !body || typeof body.animate !== 'function') return;
      summary.addEventListener('click', function (event) {
        if (reduce) return;
        event.preventDefault();
        var from = body.getBoundingClientRect().height;
        var pad = getComputedStyle(body).paddingBottom;
        if (item.ymAnim) { item.ymAnim.cancel(); item.ymAnim = null; }
        var closing = item.open && !item.classList.contains('is-closing');
        if (closing) {
          item.classList.add('is-closing');
          var shut = body.animate(
            [{ height: from + 'px', opacity: 0.88, paddingBottom: pad }, { height: '0px', opacity: 0, paddingBottom: '0px' }],
            { duration: 300, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
          );
          item.ymAnim = shut;
          var done = false;
          var finish = function () {
            if (done || item.ymAnim !== shut) return;
            done = true;
            item.open = false;
            item.classList.remove('is-closing');
            item.ymAnim = null;
            shut.cancel();
          };
          shut.onfinish = finish;
          setTimeout(finish, 420);
        } else {
          item.classList.remove('is-closing');
          if (!item.open) from = 0;
          item.open = true;
          var to = body.getBoundingClientRect().height;
          var fullPad = getComputedStyle(body).paddingBottom;
          var grow = body.animate(
            [{ height: from + 'px', opacity: from ? 0.88 : 0, paddingBottom: from ? fullPad : '0px' }, { height: to + 'px', opacity: 0.88, paddingBottom: fullPad }],
            { duration: 380, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' }
          );
          item.ymAnim = grow;
          grow.onfinish = function () { item.ymAnim = null; };
        }
      });
    });
  }

  function init() {
    syncHeaderOffset();
    setupFaq(document);
    setupSpy();
    handleInitialHash();
    cleanContactUrl();
    document.addEventListener('click', handleAnchorClick);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('resize', syncHeaderOffset, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { setupFaq(e.target); });
})();